import React from 'react';
import type { AddressType } from '@sweetgreen/garnish';
import {
  AddressOrder,
  ListActionPressable,
  usePressableState,
} from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import type { PartialRestaurant } from '../../graphql/partials';
import type { Address, DeliveryOrderDetailInput } from '../../graphql/types';
import { useActiveRouteName } from '../../navigation';
import { useChangeLocation } from '../../screens/ReorderingScreen/hooks';
import { useShouldFinishReorderInMenu } from '../../screens/ReorderingScreen/ReorderingScreen.navigation';

export const DeliveryAddressListAction = ({
  orderId,
  orderType,
  deliveryAddress,
  deliveryOrderDetail,
  restaurant,
  canChangeLocation = () => true,
}: DeliveryAddressListActionProps) => {
  const isOnMenuScreens = useShouldFinishReorderInMenu();
  const isOnCheckout = useActiveRouteName() === 'Checkout';
  const changeLocation = useChangeLocation();

  const {
    id: restaurantId,
    slug: restaurantSlug,
    name: restaurantName,
  } = restaurant ?? {};

  const handlePress = React.useCallback(() => {
    if (!canChangeLocation()) return;
    changeLocation({
      remainOnCheckout: isOnCheckout,
      finishOnMenu: isOnMenuScreens,
      orderId,
      orderType,
      deliveryOrderDetail,
      restaurantId,
      restaurantSlug,
      restaurantName,
      userInitiated: true,
    });
  }, [
    isOnMenuScreens,
    isOnCheckout,
    orderId,
    orderType,
    deliveryOrderDetail,
    restaurantId,
    restaurantSlug,
    restaurantName,
    changeLocation,
    canChangeLocation,
  ]);

  const { t } = useLocalizationContext();
  const ref = React.useRef(null);
  const { isInteracting } = usePressableState(ref);

  return (
    <ListActionPressable
      ref={ref}
      key={deliveryAddress?.id}
      onPress={handlePress}
      accessibilityRole="button"
      accessibilityLabel={t('account.addresses.address-label', {
        address: deliveryAddress?.name,
      })}
      accessibilityHint={t('account.addresses.address-hint')}
      testID={`account.addresses.${deliveryAddress?.name}-card-pressable`}
    >
      <AddressOrder
        isInteracting={isInteracting}
        addressType="delivery"
        {...deliveryAddress}
      />
    </ListActionPressable>
  );
};

// ––– TYPES –––––––––––––––––––––––––––––––––––––––––––––––––––––––––––––

type DeliveryAddressListActionProps = {
  readonly orderId?: string;
  readonly deliveryAddress?: Partial<Address>;
  readonly deliveryOrderDetail?: Partial<DeliveryOrderDetailInput>;
  readonly orderType: AddressType;
  readonly restaurant?: Partial<PartialRestaurant>;
  readonly canChangeLocation?: () => boolean;
};
